'use client'

import { IPopup, Language } from '@/lib/interfaces/interfaces'
import { Dialog, DialogContent, Typography, Box, Divider, IconButton } from '@mui/material'
import Image from 'next/image'
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { roboto } from '@/lib/fonts'
import '@/sass/menu/MenuPopupCarousel.scss'

// Tracks which bar slugs have already shown their popup during this page session.
const shownPopupSlugs = new Set<string>()

interface Props {
  popups: IPopup[];
  language: Language;
  slug: string;
}

const SWIPE_THRESHOLD = 50

const PopupSlide = ({ popup, language }: { popup: IPopup; language: Language }) => {
  const title = popup[`title_${language}`] || ''
  const bodyText = popup[`bodyText_${language}`] || ''
  const imgUrl = popup.img?.url || ''

  return (
    <div className='menu-popup-slide'>
      {imgUrl && (
        <Box sx={{ width: '100%', position: 'relative' }}>
          <Image
            src={imgUrl}
            alt={title || 'Imagen del Popup'}
            width={600}
            height={400}
            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
            loading='lazy'
          />
        </Box>
      )}
      {(title || bodyText) && (
        <DialogContent sx={{ paddingBottom: '20px', overflow: 'visible' }}>
          {title && (
            <Typography textAlign='center' variant='h5' gutterBottom>
              {title}
            </Typography>
          )}
          {!imgUrl && title && bodyText && <Divider sx={{ marginBottom: '20px', marginTop: '10px' }} />}
          {bodyText && (
            <Box textAlign='center' sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
              <div style={roboto.style}>{bodyText}</div>
            </Box>
          )}
        </DialogContent>
      )}
      {!title && !bodyText && !imgUrl && (
        <DialogContent sx={{ paddingBottom: '20px' }}>
          <Typography textAlign='center' variant='body2' color='text.secondary'>—</Typography>
        </DialogContent>
      )}
    </div>
  )
}

const MenuPopup = ({ popups, language, slug }: Props) => {
  const slides = popups.filter(p => p.isActive)
  const count = slides.length

  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [slideHeights, setSlideHeights] = useState<number[]>([])
  const startXRef = useRef<number | null>(null)
  const startYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const viewportRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (slides.length === 0) return
    if (shownPopupSlugs.has(slug)) return
    shownPopupSlugs.add(slug)
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keyboard navigation (only when multiple and open). Ignore if the focused
  // element is a text-editing control, so arrow keys are never hijacked there.
  useEffect(() => {
    if (!open || count <= 1) return
    const isEditableTarget = (e: KeyboardEvent) => {
      const node = e.target as HTMLElement | null
      if (!node) return false
      const tag = node.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable
    }
    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setCurrent(c => Math.max(0, c - 1))
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setCurrent(c => Math.min(count - 1, c + 1))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, count])

  // Measure every slide's content height into state so the height can be
  // applied in the same render as the transform (no post-paint flicker) and
  // shorter popups don't leave white space below them. Each slide wrapper gets
  // the current slide's height and clips its own overflow, so taller hidden
  // slides don't stretch the dialog. MUI Dialog mounts its content in a later
  // commit than the parent's effects, so retry until the carousel is in the DOM.
  useEffect(() => {
    if (!open || count <= 1) return
    let disposed = false
    let raf = 0
    let ro: ResizeObserver | null = null

    const measure = () => {
      const viewport = viewportRef.current
      if (!viewport) return
      const slideEls = Array.from(viewport.querySelectorAll('.menu-popup-track-slide'))
      if (slideEls.length !== count) return
      const heights = slideEls.map(s => {
        const inner = s.querySelector('.menu-popup-slide') as HTMLElement | null
        return inner ? inner.offsetHeight : 0
      })
      setSlideHeights(prev => {
        if (prev.length === heights.length && prev.every((v, i) => v === heights[i])) return prev
        return heights
      })
    }

    const start = () => {
      if (disposed) return
      const viewport = viewportRef.current
      if (!viewport) {
        raf = requestAnimationFrame(start)
        return
      }
      measure()
      ro = new ResizeObserver(measure)
      viewport.querySelectorAll('.menu-popup-slide').forEach(el => ro?.observe(el))
    }

    raf = requestAnimationFrame(start)
    return () => {
      disposed = true
      cancelAnimationFrame(raf)
      ro?.disconnect()
    }
  }, [open, count])

  // Re-measure the current slide right before paint on every navigation so its
  // height reflects its own scrollbar state (a tall slide reserves scrollbar
  // space and narrows the content ~17px; a short one doesn't). Runs in a layout
  // effect so the correction is applied before the frame is drawn — no visible snap.
  useLayoutEffect(() => {
    if (!open || count <= 1) return
    const viewport = viewportRef.current
    const slide = viewport?.querySelectorAll('.menu-popup-track-slide')[current]
    const inner = slide?.querySelector('.menu-popup-slide') as HTMLElement | null
    if (!viewport || !inner) return
    const h = inner.offsetHeight
    setSlideHeights(prev => {
      if (prev[current] === h && prev.length === count) return prev
      const next = [...prev]
      next[current] = h
      return next
    })
  }, [current, open, count])

  const goPrev = useCallback(() => setCurrent(c => Math.max(0, c - 1)), [])
  const goNext = useCallback(() => setCurrent(c => Math.min(count - 1, c + 1)), [count])

  const handlePointerDown = (e: React.PointerEvent) => {
    if (count <= 1) return
    startXRef.current = e.clientX
    startYRef.current = e.clientY
    setIsDragging(true)
    // capture to get move even outside
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || startXRef.current === null || startYRef.current === null) return
    const diffX = e.clientX - startXRef.current
    const diffY = e.clientY - startYRef.current
    // If vertical movement dominates, let native vertical scroll happen — ignore horizontal drag
    if (Math.abs(diffY) > Math.abs(diffX)) return
    // Resistance at edges (stop behavior, not loop): dampen if at first and dragging right, or last and dragging left
    let damped = diffX
    if ((current === 0 && diffX > 0) || (current === count - 1 && diffX < 0)) {
      damped = diffX * 0.35
    }
    dragOffsetRef.current = damped
    setDragOffset(damped)
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return
    setIsDragging(false)
    const diff = dragOffsetRef.current
    startXRef.current = null
    startYRef.current = null
    dragOffsetRef.current = 0
    setDragOffset(0)
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff < 0 && current < count - 1) {
        setCurrent(c => c + 1)
      } else if (diff > 0 && current > 0) {
        setCurrent(c => c - 1)
      }
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (count <= 1) return
    startXRef.current = e.touches[0].clientX
    startYRef.current = e.touches[0].clientY
    setIsDragging(true)
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || startXRef.current === null || startYRef.current === null) return
    const diffX = e.touches[0].clientX - startXRef.current
    const diffY = e.touches[0].clientY - startYRef.current
    if (Math.abs(diffY) > Math.abs(diffX)) return
    let damped = diffX
    if ((current === 0 && diffX > 0) || (current === count - 1 && diffX < 0)) {
      damped = diffX * 0.35
    }
    dragOffsetRef.current = damped
    setDragOffset(damped)
  }
  const handleTouchEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    const diff = dragOffsetRef.current
    startXRef.current = null
    startYRef.current = null
    dragOffsetRef.current = 0
    setDragOffset(0)
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff < 0 && current < count - 1) setCurrent(c => c + 1)
      else if (diff > 0 && current > 0) setCurrent(c => c - 1)
    }
  }

  if (slides.length === 0) return null

  const isSingle = count === 1
  const currentHeight = slideHeights[current] > 0 ? slideHeights[current] : undefined

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-label='Popup promocional'
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '10px',
          maxWidth: '400px',
          width: '90%',
          position: 'relative',
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Close button */}
      <span
        onClick={() => setOpen(false)}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          cursor: 'pointer',
          background: '#fff',
          border: 'none',
          padding: '0px',
          margin: '0px',
          zIndex: 20,
          color: 'gray',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '30px',
          height: '30px',
          borderRadius: '50%'
        }}
        aria-label='Cerrar popup'
      >
        <X size={24} />
      </span>

      {isSingle ? (
        <div className='menu-popup-single-scroll'>
          <PopupSlide popup={slides[0]} language={language} />
        </div>
      ) : (
        <div className='menu-popup-carousel' role='region' aria-roledescription='carousel' aria-label={`Popups promocionales, ${count} en total`}>
          {/* Viewport */}
          <div
            ref={viewportRef}
            className='menu-popup-viewport'
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'pan-y' }}
          >
            <div
              className='menu-popup-track'
              style={{
                transform: `translateX(calc(-${current * 100}% + ${dragOffset}px))`,
                transition: isDragging ? 'none' : 'transform 300ms ease',
                height: currentHeight !== undefined ? `${currentHeight}px` : undefined
              }}
            >
              {slides.map((p, i) => (
                <div
                  key={p.id}
                  role='tabpanel'
                  id={`popup-tabpanel-${i}`}
                  aria-labelledby={`popup-tab-${i}`}
                  aria-hidden={i !== current}
                  className='menu-popup-track-slide'
                  style={{ height: currentHeight !== undefined ? `${currentHeight}px` : undefined }}
                >
                  <PopupSlide popup={p} language={language} />
                </div>
              ))}
            </div>
          </div>

          {/* Arrows - hidden at edges (stop) */}
          <IconButton
            className='menu-popup-arrow menu-popup-arrow--prev'
            onClick={goPrev}
            disabled={current === 0}
            aria-label='Popup anterior'
            size='small'
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-disabled': { opacity: 0, pointerEvents: 'none' },
              zIndex: 5
            }}
          >
            <ChevronLeft size={20} />
          </IconButton>
          <IconButton
            className='menu-popup-arrow menu-popup-arrow--next'
            onClick={goNext}
            disabled={current === count - 1}
            aria-label='Popup siguiente'
            size='small'
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': { backgroundColor: '#fff' },
              '&.Mui-disabled': { opacity: 0, pointerEvents: 'none' },
              zIndex: 5
            }}
          >
            <ChevronRight size={20} />
          </IconButton>

          {/* Dots */}
          <div className='menu-popup-dots' role='tablist' aria-label='Seleccionar popup'>
            {slides.map((_, i) => (
              <button
                key={i}
                role='tab'
                id={`popup-tab-${i}`}
                aria-selected={i === current}
                aria-controls={`popup-tabpanel-${i}`}
                aria-label={`Ir al popup ${i + 1} de ${count}`}
                className={`menu-popup-dot${i === current ? ' active' : ''}`}
                onClick={() => setCurrent(i)}
              />
            ))}
          </div>
        </div>
      )}
    </Dialog>
  )
}

export default MenuPopup
